import React from "react";
import { useSnackbar } from "notistack";
import Modal from "components/shared/Modal";
import { usePropertyActivationCheck, useReonboardProperty } from "./hooks/usePropertyData";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";

interface PropertyReonboardModalProps {
  show: boolean;
  onHide: () => void;
  propertyExternalId: string;
}

export default function PropertyReonboardModal({
  show,
  onHide,
  propertyExternalId,
}: PropertyReonboardModalProps) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: activationCheck,
    isError: activationCheckError,
    isLoading: activationCheckLoading,
  } = usePropertyActivationCheck(propertyExternalId, show);
  const reonboardMutation = useReonboardProperty(propertyExternalId);

  const handleConfirm = () => {
    reonboardMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar("Re-onboard queued. Check the Sync Records tab for progress and results.", {
          variant: "success",
        });
        onHide();
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.message || "Failed to reonboard property";
        enqueueSnackbar(message, { variant: "error" });
      },
    });
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Re-onboard Property</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 20 }}>
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            This will queue guest access reconciliation for every guest with existing access records at
            this property, using their current unit/lease assignments as the source of truth. The
            reconciliation runs in the background; progress and results can be tracked from the Sync
            Records tab.
          </p>
          {activationCheckLoading ? (
            <div className="flex items-center gap-1">
              <Spinner />
              <span className="text-sm">Checking affected guest count...</span>
            </div>
          ) : activationCheckError ? (
            <Alert variant="warning">
              Unable to determine the affected guest count. Re-onboarding will
              still reconcile all existing access records for this property.
            </Alert>
          ) : (
            <Alert variant="info">
              {activationCheck?.guestCount ?? 0} guest(s) currently have access records for this property
              and will be reconciled.
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={onHide}>
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={reonboardMutation.isPending}>
          {reonboardMutation.isPending ? <Spinner className="size-5" /> : "Re-onboard"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
